// Password recovery (B6). Without a link: ask for an email and always answer generically, whether
// or not the account exists. With a link (/recover#token=…): set a new password, with the current
// two-step code when the account uses one. A reset signs the account out everywhere, so it always
// ends in a fresh sign-in. The token is held only here and cleared on every exit.
import type { DataEnvelope } from '../lib/api.ts';
import { isAbortError } from '../lib/api.ts';
import { OTP, PASSWORD_MAX, PASSWORD_MIN, isEmail, parseRecoveryRequested, parseReset } from './contract.ts';
import { type FlowDiagnostic, type FlowOptions, type FragmentSource, Flow, classifyFailure, diagnosticOf, loadIdentityCapabilities } from './flow.ts';

export type RecoveryPhase =
  | 'idle'
  | 'loading'
  | 'unavailable' // not on this server, or reset email isn't configured (for requests)
  | 'request' // ask for the email address
  | 'requesting'
  | 'requested' // generic acknowledgement; never says whether the account exists
  | 'reset' // choose a new password (link present)
  | 'resetting'
  | 'done' // password changed; a fresh sign-in is required
  | 'invalid' // the link is malformed, expired, used, or failed too many times
  | 'unknown' // the reset may or may not have happened
  | 'account_changed'
  | 'error';

export interface RecoverySnapshot {
  phase: RecoveryPhase;
  fieldError: string | null;
  /** The server refused a reset once: emphasize the verification code field. */
  codeHint: boolean;
  attempts: number;
  /**
   * The finished reset included an authenticator code. The server won't accept that code again,
   * so the next sign-in needs the app's next code.
   */
  verified: boolean;
  diagnostic: FlowDiagnostic | null;
}

const MAX_ATTEMPTS = 5;
const INITIAL: RecoverySnapshot = { phase: 'idle', fieldError: null, codeHint: false, attempts: 0, verified: false, diagnostic: null };
const TERMINAL = new Set<RecoveryPhase>(['requested', 'done', 'invalid', 'unknown', 'account_changed', 'unavailable']);

export class RecoveryFlow extends Flow<RecoverySnapshot> {
  private token: string | null = null;
  /** A fragment was present: this page load is a reset, even if the token was malformed. */
  private linked = false;
  private taken = false;
  private readonly fragments: FragmentSource;
  private readonly onSessionProblem: () => void;

  constructor(options: FlowOptions & { fragments: FragmentSource; onSessionProblem?: () => void }) {
    super(options, INITIAL);
    this.fragments = options.fragments;
    this.onSessionProblem = options.onSessionProblem ?? (() => undefined);
  }

  /** A new reset link opened in this tab. */
  restart(token: string | null): void {
    this.cancel();
    this.token = token;
    this.linked = true;
    this.set({ ...INITIAL });
    this.load();
  }

  /** From an invalid or unknown link: ask for a new email instead. */
  startOver(): void {
    this.cancel();
    this.token = null;
    this.linked = false;
    this.set({ ...INITIAL });
    this.load();
  }

  retry(): void {
    if (this.getSnapshot().phase === 'error') this.load();
  }

  wipe(): void {
    this.cancel();
    this.token = null;
  }

  protected onAttach(): void {
    if (!this.taken) {
      this.taken = true;
      const held = this.fragments.take('recover');
      if (held) {
        this.linked = true;
        this.token = held.token;
      }
    }
    const phase = this.getSnapshot().phase;
    if (phase === 'idle' || (phase === 'loading' && !this.busy)) this.load();
    else if ((phase === 'requesting' || phase === 'resetting') && !this.busy) this.set({ phase: phase === 'requesting' ? 'request' : 'reset' });
  }

  protected onSessionReady(): void {
    if (this.getSnapshot().phase === 'loading' && !this.busy) this.load();
  }

  protected onAccountChanged(): void {
    if (TERMINAL.has(this.getSnapshot().phase)) return;
    this.token = null;
    this.set({ ...INITIAL, phase: 'account_changed' });
  }

  protected onSessionRotated(): void {
    // Nothing here is bound to the browser session.
  }

  private load(): void {
    if (this.linked && !this.token) {
      this.set({ ...INITIAL, phase: 'invalid' });
      return;
    }
    this.set({ phase: 'loading', fieldError: null, diagnostic: null });
    if (!this.sessionReady || !this.attached) return;
    void this.check();
  }

  private async check(): Promise<void> {
    const { gen, signal } = this.begin();
    try {
      const caps = await loadIdentityCapabilities(this.api, signal);
      if (!this.landed(gen)) return;
      if (caps.kind === 'missing') return this.set({ phase: 'unavailable', diagnostic: caps.diagnostic });
      if (caps.kind === 'error') return this.set({ phase: 'error', diagnostic: caps.diagnostic });
      if (this.token) return this.set({ phase: 'reset', diagnostic: null });
      // Requests need email; a reset link that already arrived does not.
      if (caps.value.recovery.status !== 'enabled') return this.set({ phase: 'unavailable', diagnostic: caps.value.recovery.reason_code ? { code: caps.value.recovery.reason_code, requestId: null } : null });
      this.set({ phase: 'request', diagnostic: null });
    } catch (error) {
      if (isAbortError(error) || !this.landed(gen)) return;
      this.set({ phase: 'error', diagnostic: diagnosticOf(error) });
    }
  }

  async request(emailInput: string): Promise<void> {
    if (this.getSnapshot().phase !== 'request' || !this.sessionReady) return;
    const email = emailInput.trim();
    if (!isEmail(email)) {
      this.set({ fieldError: 'Enter the email address you sign in with.' });
      return;
    }
    const { gen, signal } = this.begin();
    this.set({ phase: 'requesting', fieldError: null, diagnostic: null });
    try {
      const env = await this.api.request<DataEnvelope<unknown>>('/identity/recovery/request', { method: 'POST', body: JSON.stringify({ email }), signal });
      if (!this.landed(gen)) return;
      if (!parseRecoveryRequested(env?.data)) {
        this.set({ phase: 'request', fieldError: 'The server’s answer wasn’t usable. Try again in a moment.', diagnostic: { code: 'invalid_response', requestId: null } });
        return;
      }
      this.set({ phase: 'requested' });
    } catch (error) {
      if (isAbortError(error) || !this.landed(gen)) return;
      const kind = classifyFailure(error);
      const diagnostic = diagnosticOf(error);
      if (kind === 'session') this.onSessionProblem();
      if (kind === 'unavailable') return this.set({ phase: 'unavailable', diagnostic });
      const fieldError =
        kind === 'input' ? 'Enter a valid email address.'
          : kind === 'limited' ? 'Too many requests. Wait a while, then try again.'
            : kind === 'session' ? 'Your browser session was refreshed. Try again.'
              : kind === 'unknown' || kind === 'network' ? 'Couldn’t confirm the server got your request. Check your connection, then try again.'
                : 'That didn’t work. Try again.';
      this.set({ phase: 'request', fieldError, diagnostic });
    }
  }

  /** `code` is the current 6-digit code, or '' when the account doesn't use two-step verification. */
  async reset(password: string, code: string): Promise<void> {
    const snap = this.getSnapshot();
    if (snap.phase !== 'reset' || !this.token || !this.sessionReady) return;
    if (password.length < PASSWORD_MIN || password.length > PASSWORD_MAX) {
      this.set({ fieldError: `Choose a password of at least ${PASSWORD_MIN} characters.` });
      return;
    }
    if (code !== '' && !OTP.test(code)) {
      this.set({ fieldError: 'A verification code is 6 digits.' });
      return;
    }
    const { gen, signal } = this.begin();
    this.set({ phase: 'resetting', fieldError: null, diagnostic: null });
    const body = { token: this.token, password, ...(code ? { mfa_code: code } : {}) };
    try {
      const env = await this.api.request<DataEnvelope<unknown>>('/identity/recovery/reset', { method: 'POST', body: JSON.stringify(body), signal });
      if (!this.landed(gen)) return;
      this.token = null;
      this.set(parseReset(env?.data) ? { phase: 'done', verified: code !== '' } : { phase: 'unknown', diagnostic: { code: 'invalid_response', requestId: null } });
    } catch (error) {
      if (isAbortError(error) || !this.landed(gen)) return;
      const kind = classifyFailure(error);
      const diagnostic = diagnosticOf(error);
      if (kind === 'session') this.onSessionProblem();
      if (kind === 'unavailable') {
        this.token = null;
        return this.set({ phase: 'unavailable', diagnostic });
      }
      if (kind === 'unknown') {
        this.token = null;
        return this.set({ phase: 'unknown', diagnostic });
      }
      if (kind === 'invalid') {
        const attempts = snap.attempts + 1;
        if (attempts >= MAX_ATTEMPTS) {
          this.token = null;
          return this.set({ phase: 'invalid', attempts, diagnostic });
        }
        return this.set({
          phase: 'reset',
          attempts,
          codeHint: true,
          fieldError: 'That didn’t work. If you sign in with an authenticator app, enter its current 6-digit code. This link may also have expired or already been used.',
          diagnostic,
        });
      }
      const fieldError =
        kind === 'input' ? `Choose a password of at least ${PASSWORD_MIN} characters.`
          : kind === 'limited' ? 'Too many attempts. Wait a while, then try again.'
            : kind === 'session' ? 'Your browser session was refreshed. Try again.'
              : kind === 'network' ? 'Couldn’t reach the server. Check your connection, then try again.'
                : 'That didn’t work. Try again.';
      this.set({ phase: 'reset', fieldError, diagnostic });
    }
  }
}
